import { useEffect, useState, FC } from 'react';
import { Table, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getLang } from 'lowcomp/plugins/member/utils';
import { textLang } from 'lowcomp/plugins/member/common/textLang';
import { useMemberTimeStore } from 'lowcode/utils/context/memberTimeZone';
import { useLuckyStore } from '../LuckyProvider';
import css from './index.module.scss';
import { NoDataIcon } from '@/components/UI/svg/Ecocredits';
import { getLuckyHistory } from '@/service/vip';
import { message } from '@/components/UI';
import getLocal from '@/app/utils/getLocal';
import { formatLocalTime } from '@/utils/timeConversionTool';

interface LuckyHistoryData {
    email: string;
    createTime: string;
    id: string;
    prizeName: string;
}

interface props {
    showPage: string;
}

const LuckyHistory: FC<props> = ({ showPage }) => {
    const { locale } = getLocal();
    const { LUCKY_ACTIVE: luckyActivety } = useLuckyStore();
    const t = getLang(textLang, locale);

    const [option, setOption] = useState<
        {
            label: string;
            value: string;
        }[]
    >([]);
    const [activeReward, setActiveReward] = useState('');
    const [historyData, setHistoryData] = useState<LuckyHistoryData[]>([]);
    const [filterData, setFilterData] = useState<LuckyHistoryData[]>([]);
    const { timezoneList } = useMemberTimeStore();

    useEffect(() => {
        if (showPage !== 'lucky_history' || !luckyActivety) return;
        getLuckyHistory(locale, luckyActivety?.id).then((res) => {
            if (res.code !== '0') return message.error(res.message);
            const optArr: string[] = res.data.content.map((item: LuckyHistoryData) => item.prizeName);
            const optionInit = Array.from(new Set(optArr)).map((item) => ({
                label: item,
                value: item,
            }));
            setOption(optionInit);
            setHistoryData(res.data.content);
        });
    }, [showPage, luckyActivety]);

    useEffect(() => {
        let arr = [...historyData];
        if (activeReward) {
            arr = arr.filter((item) => item.prizeName === activeReward);
        }
        setFilterData(arr);
    }, [activeReward, historyData]);

    const historyColumns: ColumnsType<LuckyHistoryData> = [
        {
            title: t('date'),
            dataIndex: 'createTime',
            width: 235,
            render: (val) => val && formatLocalTime(val, timezoneList),
        },
        { title: t('email'), dataIndex: 'email', width: 142 },
        { title: 'Reward', dataIndex: 'prizeName', width: 141 },
    ];

    return (
        <div className={css.lucky_history}>
            <h1 className="history-title" id="FOR-POINTS-ACTIVELY">
                Rewards List
            </h1>
            <div>
                <Select
                    className="luckyhistory_select"
                    dropdownClassName={css.luckyhistory_select_pop}
                    onChange={(val: string) => setActiveReward(val)}
                    options={option}
                    listHeight={300}
                    virtual={false}
                    allowClear
                    placeholder="Choose the Reward"
                    suffixIcon={
                        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M4.07129 6.78589H14.9284L9.49986 12.2145L4.07129 6.78589Z" fill="#949494" />
                        </svg>
                    }
                />
            </div>

            {!!filterData?.length ? (
                <>
                    <Table
                        columns={historyColumns}
                        dataSource={filterData}
                        rowKey="id"
                        className="history-tabel"
                        rowClassName={(row, i) => `${i % 2 === 1 ? 'odd' : 'even'}`}
                        pagination={false}
                        bordered={false}
                    />
                    {/* <p className="history-note">Your activity will appear here. Don&apos;t see your activity yet? Your activity will update within 24 hours.</p> */}
                </>
            ) : (
                <div className="no-content">
                    <div className="no-content-icon">
                        <NoDataIcon />
                    </div>
                    {/* <p className="no-content-note">{t('no_history_note')}</p> */}
                </div>
            )}
        </div>
    );
};

export default LuckyHistory;
